<script setup>
import ZeroBadge from '@/assets/images/badges/0-0_Badge.svg'
import AlcFreeBadge from '@/assets/images/badges/Alk.frei_Badge.svg'
import OrganicBadge from '@/assets/images/badges/Bio_Badge.svg'
import NewBadge from '@/assets/images/badges/New_Badge.svg'
import VeganBadge from '@/assets/images/badges/Vegan_Badge.svg'
import RadialProgress from '@/components/basic/atoms/RadialProgress.vue'
import { translate } from '@/plugins/i18n.js'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'

const Award = defineAsyncComponent(
  () => import('@/components/basic/atoms/Award.vue')
)

const VDP = 'vdp.svg'

const ALCOHOLIC_FLAG = 1
const DEALCOHOLISED_FLAG = 2
const ZERO_ALCOHOL_FLAG = 4

const NUTRITIONAL_BADGES = {
  zero: ZeroBadge,
  alcFree: AlcFreeBadge,
  organic: OrganicBadge,
  new: NewBadge,
  vegan: VeganBadge
}

// Props
const props = defineProps({
  product: {
    type: Object,
    default: null
  },
  threshold: {
    type: Number,
    default: 2
  },
  tooltipClass: {
    type: String,
    default: ''
  },
  placement: {
    type: String,
    default: 'right'
  },
  maxAwards: {
    type: Number,
    default: 3
  }
})

const calculatedAwards = ref([])
const fetchedProduct = ref(null)

// Search-fed products (collection pages) carry the full badge payload
// (awards, details, unit_type, first_enabled_at, alcohol_content); bulk- and
// upsell-fed products do not — for those the legacy per-product PDP fetch
// stays, and the fetched product takes precedence for badge data.
const currentProduct = computed(() => {
  return fetchedProduct.value || props.product || {}
})

const awards = computed(() => {
  return currentProduct.value.awards || []
})

const nutritionalNotes = computed(() => {
  return currentProduct.value.details?.nutritional_note || []
})

// Tags arrive as a comma string (PDP / v2 search docs) or as an array
// (meta/bulk products) — normalize for the badge keyword checks.
const tagsString = computed(() => {
  const tags = currentProduct.value.tags
  if (Array.isArray(tags)) return tags.join(',')
  return typeof tags === 'string' ? tags : ''
})

const firstEnabledAt = computed(() => {
  return currentProduct.value.first_enabled_at
})

const isZeroAlcohol = computed(() => {
  const concentration =
    currentProduct.value.nutritional_data?.wine_alcohol_concentration

  // Handle string values with units (e.g., "0,00 % vol", "0.0%")
  if (typeof concentration === 'string') {
    const cleanConcentration = concentration
      .replace(/[^\d,.]/g, '')
      .replace(',', '.')
    return Number.parseFloat(cleanConcentration) === 0
  }

  return concentration === 0 || concentration === '0' || concentration === '0.0'
})

const isAlcoholFree = computed(() => {
  return currentProduct.value.alcohol_content === DEALCOHOLISED_FLAG
})

const isZeroAlcoholFlag = computed(() => {
  return currentProduct.value.alcohol_content === ZERO_ALCOHOL_FLAG
})

const isNewProduct = computed(() => {
  if (!firstEnabledAt.value) return false

  const firstEnabledDate = new Date(firstEnabledAt.value * 1000)
  const fourteenDaysAgo = new Date()
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)
  return firstEnabledDate > fourteenDaysAgo
})

const nutritionalBadges = computed(() => {
  const badges = []

  // Check for new product
  if (firstEnabledAt.value && isNewProduct.value) {
    badges.push({
      type: 'new',
      image: NUTRITIONAL_BADGES.new,
      alt: translate('badge_new')
    })
  }

  // Check for zero alcohol flag (value 4)
  if (isZeroAlcoholFlag.value) {
    badges.push({
      type: 'zero',
      image: NUTRITIONAL_BADGES.zero,
      alt: translate('badge_zero_alcohol')
    })
  }

  // Check for alcohol-free (value 2)
  if (isAlcoholFree.value) {
    badges.push({
      type: 'alcFree',
      image: NUTRITIONAL_BADGES.alcFree,
      alt: translate('badge_alc_free')
    })
  }

  // Check for organic/bio badges in nutritional notes or tags
  const bioRegex = /\bbio\b/i
  const organicRegex = /\borganic\b/i
  if (
    nutritionalNotes.value?.some(
      (note) => note && (bioRegex.test(note) || organicRegex.test(note))
    ) ||
    (tagsString.value &&
      (bioRegex.test(tagsString.value) || organicRegex.test(tagsString.value)))
  ) {
    badges.push({
      type: 'organic',
      image: NUTRITIONAL_BADGES.organic,
      alt: translate('badge_organic')
    })
  }

  // Check for vegan badges in nutritional notes or tags
  if (
    nutritionalNotes.value?.some(
      (note) => note && note.toLowerCase().includes('vegan')
    ) ||
    tagsString.value.toLowerCase().includes('vegan')
  ) {
    badges.push({
      type: 'vegan',
      image: NUTRITIONAL_BADGES.vegan,
      alt: translate('badge_vegan')
    })
  }

  return badges
})

const nutritionalBadge = computed(() => {
  return nutritionalBadges.value.length > 0
})

const maxRegularAwards = computed(() => {
  const maxTotal = props.maxAwards
  const nutritionalBadgeCount = nutritionalBadges.value.length
  return Math.max(0, maxTotal - nutritionalBadgeCount)
})

const awardSlice = computed(() => {
  return calculatedAwards.value.slice(0, maxRegularAwards.value)
})

const position = computed(() => {
  return {
    r: props.placement === 'right',
    l: props.placement === 'left',
    t: props.placement === 'top',
    b: props.placement === 'bottom'
  }
})

const nudge = computed(() => {
  if (position.value.r) return { l: 10, r: 0 }
  if (position.value.l) return { l: 0, r: 10 }

  return { l: 0, r: 0 }
})

const loadAwards = async () => {
  const [{ default: UtilityHelper }, { default: SiteConfig, CONFIG_MAP }] =
    await Promise.all([
      import('@/services/helpers/UtilityHelper'),
      import('@/services/SiteConfig')
    ])
  const slice = awards.value.slice(0, maxRegularAwards.value)
  const skipVdp = SiteConfig.siteId !== CONFIG_MAP.de.siteId

  calculatedAwards.value = slice.reduce((awards, award) => {
    if (skipVdp && award.template === VDP) return awards

    return [
      ...awards,
      { id: `award_${UtilityHelper.getRandomHash()}`, ...award }
    ]
  }, [])
}

const fetchProductData = async () => {
  const product = props.product
  // Only non-search products (bulk/upsell-fed tiles, e.g. old category
  // pages) need the PDP round trip for their badge payload.
  if (!product?.product_id || product.from_product_search) return
  if (fetchedProduct.value?.product_id === product.product_id) return

  try {
    const [TypedApiControllerClass, { PDP_CONFIG }] = await Promise.all([
      import('@/services/TypedApiController').then((m) => m.default),
      import('@/services/helpers/ProductHelper')
    ])

    fetchedProduct.value = await new TypedApiControllerClass().fetchProduct(
      product.product_id,
      product.campaign_id ?? 1,
      PDP_CONFIG
    )
  } catch (error) {
    console.error('Error fetching product data for award wrapper:', error)
  }
}

onMounted(async () => {
  if (props.product) {
    await fetchProductData()
    loadAwards()
  }
})

watch(
  () => props.product,
  async () => {
    await fetchProductData()
    loadAwards()
  }
)
</script>

<template>
  <div v-if="product" class="pointer-events-all">
    <!-- Regular Awards -->
    <VTooltip
      :aria-label="translate('aria_label_award_wrapper')"
      v-for="{ data, template, id } in awardSlice"
      :key="id"
      :right="position.r"
      :left="position.l"
      :top="position.t"
      :bottom="position.b"
      :nudge-right="nudge.r"
      :nudge-left="nudge.l"
      :content-class="tooltipClass"
    >
      <template v-slot:activator="{ props }">
        <div class="cursor-help" v-bind="props">
          <RadialProgress
            v-if="data.score"
            :id="id"
            class="mb-2"
            :border-color="data.border_color"
            :score-val="data.score"
            :max-val="data.score_max"
          >
            <Award :data="data" :template="template" />
          </RadialProgress>
          <Award
            v-else
            :id="id"
            class="mb-2"
            :data="data"
            :template="template"
          />
        </div>
      </template>
      <span v-html="data.tooltip"></span>
    </VTooltip>

    <template v-if="['bottle', 'package'].includes(currentProduct?.unit_type)">
      <div
        v-for="badge in nutritionalBadges"
        :key="badge.type"
        class="nutritional-badge-bottom mb-2"
        :class="`nutritional-badge-${badge.type}`"
      >
        <img :src="badge.image" :alt="badge.alt" class="badge-image" />
      </div>
    </template>
  </div>
</template>

<style scoped>
:deep(.trophy) {
  width: 66px;
  height: 66px;
}

.nutritional-badge-bottom {
  display: flex;
  justify-content: center;
  align-items: center;
}

.badge-image {
  width: 66px;
  height: 66px;
  object-fit: contain;
}
</style>
